import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventPnlPath } from '@/lib/event-pnl';
import {
  BarTrendChart,
  CardHeader,
  DataTable,
  DatePicker,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  SkeletonTable,
  StatTile,
  Tabs,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { apiErrorCode, errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { EventPnlNameLink } from '@/components/event-pnl';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  FLAG_DEFAULT_SORT,
  FLAG_SEARCH_FIELDS,
  FLAG_SORT_KEYS,
  FacilitatorFlagsTable,
} from '@/components/facilitator-flags-table';
import {
  FACILITATORS_PATH,
  PERSON_ROLE_LABELS,
  SPEND_CATEGORY,
  SPEND_CATEGORY_LABELS,
  SPEND_PARAM,
  TRAVEL_CATEGORIES,
  averageLabel,
  eventDateLabel,
  salesforceStatementUrl,
  spendMoney,
  spendShare,
  toFlagRows,
  toPersonEventRows,
  unitMoney,
  useFacilitatorDetail,
  useSpendParams,
  type FacilitatorDetail,
  type PersonEventRow,
  type SpendCategory,
} from '@/lib/facilitator-spend';

/** The API's code for a person with no lines in the window. */
const NOT_FOUND = 'NOT_FOUND';

const PERSON_VIEW = {
  events: 'events',
  flags: 'flags',
} as const;
type PersonView = (typeof PERSON_VIEW)[keyof typeof PERSON_VIEW];

function parseView(value: string | null): PersonView {
  return value === PERSON_VIEW.flags ? PERSON_VIEW.flags : PERSON_VIEW.events;
}

const EVENT_SORT_FIELDS = {
  event: 'opportunityName',
  eventDate: 'eventDate',
  program: 'program',
  participants: 'participants',
  fee: 'fee',
  assistant: 'assistant',
  travel: 'travel',
  other: 'other',
  total: 'total',
  flags: 'flaggedCount',
} as const;
// Each travel column sorts by its own amount, which PersonEventRow keeps under the category key.
const EVENT_SORT_KEYS: readonly string[] = [...Object.values(EVENT_SORT_FIELDS), ...TRAVEL_CATEGORIES];
const EVENT_DEFAULT_SORT = { key: EVENT_SORT_FIELDS.eventDate, direction: 'desc' } as const;
const EVENT_SEARCH_FIELDS = ['opportunityName', 'accountName', 'program'] as const satisfies ReadonlyArray<keyof PersonEventRow>;

/** The categories the averages panel lists, fee first. */
const AVERAGE_CATEGORIES: readonly SpendCategory[] = [SPEND_CATEGORY.fee, SPEND_CATEGORY.assistant, ...TRAVEL_CATEGORIES];

/** Stacked series for the quarterly chart. */
const TREND_SERIES = [
  { key: 'fee', label: 'Fee' },
  { key: 'assistant', label: 'Assistants' },
  { key: 'travel', label: 'Travel' },
  { key: 'other', label: 'Other' },
] as const;

function money(value: number): string {
  return value === 0 ? '—' : spendMoney(value);
}

function participantsLabel(value: number | null): string {
  return value === null || value === 0 ? '—' : value.toLocaleString('en-US');
}

function Trend({ data }: { readonly data: FacilitatorDetail }): React.ReactElement {
  const points = data.trend.map((point) => ({
    key: point.quarter,
    label: point.quarter.replace('-', ' '),
    value: point.totals.total,
    details: [`${point.events} ${point.events === 1 ? 'event' : 'events'}`],
  }));
  const series = TREND_SERIES.map((entry) => ({
    key: entry.key === 'other' ? 'Other' : entry.key,
    label: entry.label,
    values: data.trend.map((point) => point.totals[entry.key]),
  }));
  return (
    <Panel className="p-5 lg:col-span-2">
      <CardHeader title="Billed by quarter" description="Fee, assistants, travel and other lines, by the quarter of the event." />
      {points.length === 0 ? (
        <EmptyState title="No dated events" message="None of these lines has an event date to chart." />
      ) : (
        <BarTrendChart className="mt-4" title="Billed by quarter" unit="billed" points={points} series={series} height={430} formatValue={spendMoney} />
      )}
    </Panel>
  );
}

function Averages({ data }: { readonly data: FacilitatorDetail }): React.ReactElement {
  return (
    <Panel className="p-5">
      <CardHeader title="Their averages" description="Per unit, beside the median across everyone in the same window." />
      <dl className="mt-4 divide-y divide-portal-line">
        {AVERAGE_CATEGORIES.map((category) => {
          const norm = data.norms[category];
          return (
            <div key={category} className="flex items-baseline justify-between gap-3 py-2">
              <dt className="text-portal-small text-portal-ink">{SPEND_CATEGORY_LABELS[category]}</dt>
              <dd className="text-right">
                <span className="block text-portal-small font-bold tabular-nums text-portal-ink">
                  {averageLabel(data.summary.averages[category])}
                </span>
                <span className="block text-portal-micro text-portal-muted">
                  {norm ? `Typical ${unitMoney(norm.median)}` : 'No norm yet'}
                </span>
              </dd>
            </div>
          );
        })}
      </dl>
    </Panel>
  );
}

function EventsTable({ rows }: { readonly rows: readonly PersonEventRow[] }): React.ReactElement {
  const { sort, onSort } = useTableSort(EVENT_SORT_KEYS, EVENT_DEFAULT_SORT);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const navigate = useNavigate();
  const cards: DataTableCard[] = sorted.map((row) => ({
    key: row.statementId,
    title: row.opportunityName,
    subtitle: `${eventDateLabel(row.eventDate)} · ${row.program}`,
    onOpen: () => navigate(eventPnlPath(row.opportunityId)),
    fields: [
      { label: 'Participants', value: participantsLabel(row.participants) },
      { label: 'Fee', value: money(row.fee) },
      { label: 'Assistants', value: money(row.assistant) },
      { label: 'Travel', value: money(row.travel) },
      ...TRAVEL_CATEGORIES.filter((category) => row[category] !== 0).map((category) => ({
        label: SPEND_CATEGORY_LABELS[category],
        value: money(row[category]),
      })),
      { label: 'Other', value: money(row.other) },
      { label: 'Total', value: spendMoney(row.total) },
      { label: 'Worth a look', value: row.flaggedCount > 0 ? String(row.flaggedCount) : 'None' },
    ],
  }));
  return (
    <DataTable
      headers={[
        { label: 'Event', sortKey: EVENT_SORT_FIELDS.event },
        { label: 'Event date', sortKey: EVENT_SORT_FIELDS.eventDate },
        { label: 'Program', sortKey: EVENT_SORT_FIELDS.program },
        { label: 'Participants', sortKey: EVENT_SORT_FIELDS.participants },
        { label: 'Fee', sortKey: EVENT_SORT_FIELDS.fee },
        { label: 'Assistants', sortKey: EVENT_SORT_FIELDS.assistant },
        { label: 'Travel', sortKey: EVENT_SORT_FIELDS.travel },
        ...TRAVEL_CATEGORIES.map((category) => ({ label: SPEND_CATEGORY_LABELS[category], sortKey: category })),
        { label: 'Other', sortKey: EVENT_SORT_FIELDS.other },
        { label: 'Total', sortKey: EVENT_SORT_FIELDS.total },
        { label: 'Worth a look', sortKey: EVENT_SORT_FIELDS.flags },
        'Statement',
      ]}
      // Participants through Worth a look: everything between Program and Statement.
      rightAlignedHeaders={Array.from({ length: TRAVEL_CATEGORIES.length + 7 }, (_, index) => index + 3)}
      sort={sort}
      onSort={onSort}
      minWidth={2120}
      cards={cards}
    >
      {sorted.map((row) => (
        <tr key={row.statementId} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small">
            <EventPnlNameLink opportunityId={row.opportunityId} name={row.opportunityName} bold />
            <span className="block text-portal-micro text-portal-muted">{row.accountName}</span>
          </td>
          <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{eventDateLabel(row.eventDate)}</td>
          <td className="p-3 text-portal-small text-portal-muted">{row.program}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">{participantsLabel(row.participants)}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">{money(row.fee)}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">{money(row.assistant)}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums">{money(row.travel)}</td>
          {TRAVEL_CATEGORIES.map((category) => (
            <td key={category} className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">
              {money(row[category])}
            </td>
          ))}
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">{money(row.other)}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">
            {spendMoney(row.total)}
          </td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
            {row.flaggedCount > 0 ? <span className="font-bold text-amber-800">{row.flaggedCount}</span> : <span className="text-portal-muted">—</span>}
          </td>
          <td className="whitespace-nowrap p-3 text-portal-small">
            <a href={salesforceStatementUrl(row.statementId)} target="_blank" rel="noreferrer" className="text-brand hover:underline">
              Open
            </a>
          </td>
        </tr>
      ))}
    </DataTable>
  );
}

/**
 * Analytics → Accounting → Facilitators → one person: every event they
 * worked with what they billed per category, their spend by quarter, their
 * averages beside the norm, and their lines worth a look. "Program" is only
 * where a line was billed, not a list of what they deliver.
 */
export function FacilitatorDetailPage(): React.ReactElement {
  const { personId = '' } = useParams();
  const { params, window, windowSearch, setParam } = useSpendParams();
  const view = parseView(params.get(SPEND_PARAM.view));
  const { search, setSearch } = useUrlSearch();
  const { sort: flagSort, onSort: onFlagSort } = useTableSort(FLAG_SORT_KEYS, FLAG_DEFAULT_SORT);
  const detail = useFacilitatorDetail(window, personId);

  const eventRows = useMemo(
    () => filterRows(toPersonEventRows(detail.data?.events ?? []), search, EVENT_SEARCH_FIELDS),
    [detail.data, search],
  );
  const flagRows = useMemo(
    () => sortRows(filterRows(toFlagRows(detail.data?.flags ?? []), search, FLAG_SEARCH_FIELDS), flagSort),
    [detail.data, search, flagSort],
  );

  const backTo = `${FACILITATORS_PATH.list}${windowSearch}`;
  const summary = detail.data?.summary;
  const name = summary ? summary.person.name || summary.person.email || 'Unnamed contact' : 'Facilitator';
  const filtered = search.trim() !== '';

  function setView(next: PersonView): void {
    setParam({ [SPEND_PARAM.view]: next === PERSON_VIEW.events ? '' : next, sort: '', dir: '' });
  }

  return (
    <section className="animate-portal-rise">
      <PageHeading
        backTo={backTo}
        breadcrumbs={[{ label: 'Accounting' }, { label: 'Facilitators', to: backTo }]}
        title={name}
        subtitle={
          summary
            ? `${PERSON_ROLE_LABELS[summary.role]}${summary.person.email ? ` · ${summary.person.email}` : ''} · events ${eventDateLabel(detail.data?.window.from ?? null)} to ${eventDateLabel(detail.data?.window.to ?? null)}`
            : undefined
        }
      />

      {detail.isError && apiErrorCode(detail.error) !== NOT_FOUND ? (
        <ErrorNotice title="This person’s spend is unavailable" message={errorMessage(detail.error)} />
      ) : detail.isPending ? (
        <SkeletonTable label="Loading their events…" />
      ) : detail.data && summary ? (
        <>
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Total paid"
              value={spendMoney(summary.totals.total)}
              hint={`${summary.events} events, ${summary.participants.toLocaleString('en-US')} participants`}
            />
            <StatTile
              label="Avg fee per event"
              value={summary.avgFeePerEvent === null ? '—' : spendMoney(summary.avgFeePerEvent)}
              hint={detail.data.norms.fee ? `Typical ${spendMoney(detail.data.norms.fee.median)}` : 'No fee lines'}
            />
            <StatTile label="Travel share" value={spendShare(summary.travelShare)} hint={`${spendMoney(summary.totals.travel)} of travel`} />
            <StatTile
              label="Worth a look"
              value={spendMoney(summary.flaggedAmount)}
              hint={`${summary.flaggedCount} ${summary.flaggedCount === 1 ? 'line' : 'lines'}, ${spendMoney(summary.flaggedExcess)} above the norm`}
            />
          </div>

          <div className="mb-4 grid gap-4 lg:grid-cols-3">
            <Trend data={detail.data} />
            <Averages data={detail.data} />
          </div>

          <Tabs<PersonView>
            label="Their events and flags"
            active={view}
            onChange={setView}
            items={[
              { key: PERSON_VIEW.events, label: 'Events', count: detail.data.events.length },
              { key: PERSON_VIEW.flags, label: 'Worth a look', count: detail.data.flags.length },
            ]}
          />
          <div className="my-4 flex flex-wrap items-end gap-3">
            <SearchInput
              label={view === PERSON_VIEW.events ? 'Search events' : 'Search flagged lines'}
              placeholder="Event, client or program"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-xs"
            />
            <div>
              <label htmlFor="person-from" className="mb-1 block text-portal-micro font-bold text-portal-muted">
                Events from
              </label>
              <DatePicker id="person-from" value={window.from} placeholder="Start" onChange={(value) => setParam({ [SPEND_PARAM.from]: value })} />
            </div>
            <div>
              <label htmlFor="person-to" className="mb-1 block text-portal-micro font-bold text-portal-muted">
                To
              </label>
              <DatePicker id="person-to" value={window.to} placeholder="Today" onChange={(value) => setParam({ [SPEND_PARAM.to]: value })} />
            </div>
          </div>

          {view === PERSON_VIEW.events ? (
            eventRows.length === 0 ? (
              <Panel>
                <EmptyState title="No events match" message="Try a different search." />
              </Panel>
            ) : (
              <EventsTable rows={eventRows} />
            )
          ) : flagRows.length === 0 ? (
            <Panel>
              {filtered ? (
                <EmptyState title="No flagged lines match" message="Try a different search." />
              ) : (
                <EmptyState title="Nothing stands out" message="Every line they billed in this window is within the norm for its category." />
              )}
            </Panel>
          ) : (
            <FacilitatorFlagsTable rows={flagRows} sort={flagSort} onSort={onFlagSort} showPerson={false} />
          )}
        </>
      ) : (
        <Panel>
          <EmptyState title="Nothing billed in this window" message="Widen the dates to see this person’s events." />
        </Panel>
      )}
    </section>
  );
}
